/**
 * goNXT Technology (Pty) Ltd — company identity used across legal pages,
 * footer, emails, and DSAR flows. Single source of truth: update here and
 * every surface that references the company picks up the change.
 *
 * Verifiable facts are sourced from the public gonxt.tech website.
 * Identifiers that are NOT publicly disclosed (CIPC reg number, VAT,
 * Information Officer name) are set to `null` and gracefully hidden in
 * the UI, with instructions to contact legal@gonxt.tech. Replace `null`
 * with the real values before wide GA — required by POPIA s.18.
 */

export const COMPANY_INFO = {
  /** Full legal name exactly as used on gonxt.tech public footer. */
  legalName: 'goNXT Technology (Pty) Ltd',
  /** Short display / brand name. */
  tradingName: 'goNXT',
  /** Product name. */
  productName: 'MoreMe AI',
  /** Product tagline used on marketing / login. */
  productTagline: 'Enterprise Learning Platform',

  // ─── Legal identifiers (POPIA s.18 disclosures) ────────────────────
  // `null` means "not yet configured" — footer + legal pages render a
  // "provided on request" line instead of a fake value. Format for CIPC
  // reg: YYYY/NNNNNN/07. Format for SARS VAT: "4" + 9 digits.
  registrationNumber: null as string | null,
  vatNumber:          null as string | null,

  // ─── Physical address ──────────────────────────────────────────────
  // Public disclosure on gonxt.tech homepage: "Lanseria Corporate Park,
  // Johannesburg". Specific unit / postal code to be confirmed.
  registeredAddress: {
    line1: 'Lanseria Corporate Park',
    line2: '',
    city: 'Johannesburg',
    province: 'Gauteng',
    postalCode: '',
    country: 'South Africa',
  },

  // ─── POPIA Information Officer (s.55-57) ───────────────────────────
  // POPIA s.56(1): where no-one is formally designated, the head of the
  // private body is automatically the Information Officer. We therefore
  // route enquiries to the role (Managing Director) via privacy@gonxt.tech
  // until a specific person is registered with the Information Regulator.
  informationOfficer: {
    name: null as string | null,
    role: 'Managing Director',
    email: 'privacy@gonxt.tech',
  },

  // ─── Contact addresses ─────────────────────────────────────────────
  // Primary public contact (info@) is what gonxt.tech surfaces; the
  // other aliases are best-practice separate mailboxes for POPIA +
  // security workflows and should be provisioned on the gonxt.tech
  // domain.
  emails: {
    support:    'info@gonxt.tech',
    privacy:    'privacy@gonxt.tech',     // POPIA enquiries (Info Officer)
    legal:      'legal@gonxt.tech',
    dpo:        'privacy@gonxt.tech',     // alias for Information Officer
    security:   'security@gonxt.tech',    // vulnerability + breach reports
    billing:    'billing@gonxt.tech',
  },

  // ─── Web ────────────────────────────────────────────────────────────
  website: 'https://www.gonxt.tech',

  // ─── Jurisdiction ──────────────────────────────────────────────────
  governingLaw: 'Republic of South Africa',
  courts: 'High Court of South Africa, Gauteng Division, Johannesburg',
  currency: 'ZAR',
  currencySymbol: 'R',
  locale: 'en-ZA',
  timezone: 'Africa/Johannesburg',

  // ─── Information Regulator (POPIA complaint body) ──────────────────
  informationRegulator: {
    name: 'Information Regulator (South Africa)',
    address: 'JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001',
    email: 'enquiries@inforegulator.org.za',
    complaintsEmail: 'complaints.IR@inforegulator.org.za',
    website: 'https://inforegulator.org.za',
  },
} as const;

/** True when a string identifier hasn't been filled in yet. */
export function isPlaceholder(v: string | null | undefined): boolean {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  if (s === '') return true;
  if (s.startsWith('TODO')) return true;
  return false;
}

/**
 * Convenience helper — full formatted registered address for footers /
 * docs. Skips any empty lines so "to be provided" postal codes don't
 * render as "... , , South Africa".
 */
export function formattedAddress(): string {
  const a = COMPANY_INFO.registeredAddress;
  return [a.line1, a.line2, a.city, a.province, a.postalCode, a.country]
    .map((x) => (x || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Copyright line with current-year default. */
export function copyrightLine(year: number = new Date().getFullYear()): string {
  return `© ${year} ${COMPANY_INFO.legalName}. All rights reserved.`;
}

/**
 * Human-readable description of the Information Officer. Falls back to
 * the role-only form mandated by POPIA s.56(1) where no specific person
 * is yet registered.
 */
export function informationOfficerDescription(): string {
  const io = COMPANY_INFO.informationOfficer;
  if (isPlaceholder(io.name)) {
    return `${io.role} (automatically the Information Officer under POPIA s.56(1) until a specific person is designated)`;
  }
  return `${io.name}, ${io.role}`;
}

export type CompanyInfo = typeof COMPANY_INFO;
